// Module `projects` (API-v3 6): projects and setup, running-project import,
// home, search, notifications, activity log. Owned by WP projects.
import { z } from "zod";
import { ACTIVITY_TYPES, AUDIT_MODULES, IMPORT_PILES, PROJECT_FUNCTIONS, SETUP_STEPS, type AttentionAudience, type ImportPile, type NotificationKind, type ProjectFunction, type SetupStep } from "../enums";
import { LIMITS } from "../limits";
import { PROJECT_PERMISSIONS, type PermissionPreset, type ProjectPermission } from "../permissions";
import { formatNumber } from "../money";
import { uuid } from "../schemas/common";
import { templateSection, type InvitationV3, type TemplateSection } from "./access";
import type { BudgetView, WaitingSplit } from "./admin";
import type { DraftProjectRow, OnboardingSection } from "./onboarding";
import {
  centsV3,
  email,
  isoDate,
  text,
  textOrEmpty,
  type FileRef,
  type HistoryEntry,
  type Id,
  type ProjectRef,
  type StatusChip,
  type UserRef,
} from "./common";

// ---------- 6.1 projects and setup ----------

export const projectContractBody = z.object({
  name: text(LIMITS.contractName, "Project name"),
  reference: text(LIMITS.contractReference, "Reference"),
  kind: z.enum(["service_fee_based", "grant"]),
  signedContractFileId: uuid.nullable(),
  funder: textOrEmpty(LIMITS.funder),
  funderContact: textOrEmpty(LIMITS.funderContactName),
  funderContactEmail: email.nullable(),
  torDecisionDays: z.number().int().min(1).max(365).default(6),
  envelopeCents: centsV3(LIMITS.envelopeCentsMax).nullable(),
  cofinancingPct: z.number().min(0).max(100).nullable(),
  startDate: isoDate.nullable(),
  endDate: isoDate.nullable(),
  leadUserId: uuid.nullable(),
  partners: z.array(text(LIMITS.partner)).max(LIMITS.partnersMax),
  approvalClockDays: z.number().int().min(1).max(365).optional(),
  paymentDays: z.number().int().min(1).max(365).optional(),
  /** Onboarding Task 2.1: who signs for the funder, and the contract's currency. */
  contractingAuthority: textOrEmpty(LIMITS.funder).optional(),
  currency: z.string().regex(/^[A-Z]{3}$/).optional(),
});
export type ProjectContractBody = z.infer<typeof projectContractBody>;
export const projectContractPatch = projectContractBody.partial();

export type ProjectRow = ProjectRef & {
  subLine: string;
  typeLabel: "Service contract" | "Grant";
  period: { start: string | null; end: string | null };
  yourRole: string;
  isCurrent: boolean;
};
export type ClosedProjectRow = ProjectRef & { closedMonth: string };
export type ProjectList = {
  open: ProjectRow[];
  closed: ClosedProjectRow[];
  canCreate: boolean;
  /**
   * ST-N6: the organisation's hidden holding project, "Contacts from before
   * projects were separated" (owners and admins only, when it has people).
   */
  holding?: { id: string; name: string; people: number } | null;
  /**
   * Onboarding workspace: the caller's own draft projects (made by the
   * onboarding, not started yet), newest first. Drafts are never in `open`
   * or `closed`, and nobody but their creator sees them here.
   */
  drafts?: DraftProjectRow[];
};
/** ST-N6: move a person out of the holding project into one project (org owners and admins). */
export const moveFromHoldingBody = z.object({ projectId: uuid });
export type MoveFromHoldingResult = { path: string };
export type ProjectDetail = ProjectContractBody &
  ProjectRef & {
    status: "open" | "closed";
    archivedAt: string | null;
    lead: UserRef | null;
    typeLabel: string;
    torDecisionDaysComputed: number | null;
    takeoverDate: string | null;
    signedContractFile: FileRef | null;
    canEdit: boolean;
    isLead: boolean;
    /** ST-N6: the organisation's holding project ("Contacts from before projects were separated"). */
    holding?: boolean;
  };
export type ContractFileReading = {
  reference: string | null;
  startDate: string | null;
  endDate: string | null;
  envelopeCents: number | null;
  funder: string | null;
  /** The file reads as an application form or concept note, not a signed contract (R5 #5). */
  looksLikeApplication: boolean;
};

export const setupStep = z.enum(SETUP_STEPS);
export type SetupState = {
  steps: Record<SetupStep, "todo" | "done" | "skipped">;
  /**
   * invited: people on the team other than the contract lead, plus open invitations.
   * onTeam, invitations (SE-N2): the people the project's Team page lists (owners and admins
   * included) and its open invitations, so every screen gives the same count (teamCountText).
   */
  counts: { results: number; activities: number; indicators: number; invited: number; onTeam: number; invitations: number; keyExperts: number; cvMissing: number };
};
/** "4 people on the team, 1 invited": the one wording of the project team's size (SE-N2). */
export function teamCountText(onTeam: number, invitations: number): string {
  const people = `${formatNumber(onTeam)} ${onTeam === 1 ? "person" : "people"} on the team`;
  return invitations > 0 ? `${people}, ${formatNumber(invitations)} invited` : people;
}
/** The setup checklist of every project (Administration, "Set up the project"), computed from the project's data. */
export const SETUP_CHECK_KEYS = ["contract", "howItWorks", "results", "linesOfWork", "budget", "team", "partners", "experts", "stakeholders", "firstEvent", "torTemplate", "reportingCalendar"] as const;
export type SetupCheckKey = (typeof SETUP_CHECK_KEYS)[number];
export type SetupChecklist = {
  steps: {
    key: SetupCheckKey;
    label: string;
    /** What is there, or what is left, in one line. */
    detail: string;
    done: boolean;
    /** Not every project needs it (partners): it does not count towards "complete". */
    optional: boolean;
    /** Round 4, OB-F17: where to fix what is left, when the step knows better than its usual screen ("Upload the CV of Ana"). */
    fix?: { path: "expert" | "experts"; expertId?: string; label: string };
  }[];
  /** Steps done of the steps that count. */
  done: number;
  total: number;
  complete: boolean;
  /** Hidden from the Administration overview (only once complete). */
  hidden: boolean;
  /** About how long the steps still to do take, in minutes (Suite Setup Checklist "About 20 minutes left"). */
  minutesLeft?: number;
};
export const setupPatch = z.object({ step: setupStep, state: z.enum(["done", "skipped"]) });

export type LogframePreview = {
  previewId: Id;
  fileName: string;
  summary: { results: number; activities: number; indicators: number };
  rows: {
    rowId: string;
    code: string;
    title: string;
    type: "result" | "event" | "deliverable" | "nke" | "indicator";
    exists: boolean;
    create: boolean;
    resultCode: string | null;
    baseline: string | null;
    target: string | null;
    unit: string | null;
    /** Suite Import Logframe: a suggested short name (results and indicators), the level of a result, how an indicator is measured. */
    shortName?: string | null;
    levelNo?: number | null;
    kind?: "count" | "share_from_answers" | "reading" | null;
    /** IM-N2, indicators: the funder indicator(s) its text names ("FPI 27"), as it will be reported; null = none. */
    funderCode?: string | null;
    /** R5: the code of the result it feeds into, as read from the layout (Outcome 1 under Impact); null = not said. */
    parentCode?: string | null;
    /** R5 #7, #15a, activities: every output it feeds, by code. */
    outputCodes?: string[] | null;
    /** Activities from the activity matrix: the group ("A"), the number of cost lines and their total. */
    group?: string | null;
    costLines?: number;
    costTotalCents?: number | null;
  }[];
  /** GL4: "rules" = the built-in reading; "ai" = the AI reading the user asked for after the rules found nothing. */
  readBy?: "rules" | "ai";
};
export const logframeImportBody = z.object({ fileId: uuid });
/** GL4: may the AI read a logframe the rules could not? "ai_off" = the organisation switched it off; "budget" = this month's cap is used up; "no_ai" = not on this server. */
export type LogframeAiStatus = { on: boolean; reason: "ai_off" | "budget" | "no_ai" | null };
/** How an indicator is measured, as picked in step 3 ("checkin": a count from the check-in lists). */
export const IMPORT_INDICATOR_KINDS = ["checkin", "count", "share_from_answers", "reading"] as const;
export type ImportIndicatorKind = (typeof IMPORT_INDICATOR_KINDS)[number];
/**
 * IM-N1: the first guess of how an imported indicator is measured, the same in
 * both import routes (Results › Import logframe and New project › Logframe):
 * a count of participants comes from the check-in lists, other counts from
 * the records, shares of participants from the feedback answers.
 */
export function suggestImportKind(r: { kind?: "count" | "share_from_answers" | "reading" | null; title: string }): ImportIndicatorKind {
  return r.kind === "count" && /participant|attendee|people trained|persons? trained/i.test(r.title) ? "checkin" : (r.kind ?? "reading");
}
export const logframeCommitBody = z.object({
  rowIds: z.array(z.string().max(60)).max(2000),
  /** Step 2: the short names checked, by row id. */
  shortNames: z.record(z.string().max(60), z.string().trim().max(80)).optional(),
  /** Step 3: how each indicator is measured, by row id. */
  kinds: z.record(z.string().max(60), z.enum(IMPORT_INDICATOR_KINDS)).optional(),
  /** Step 2: the result each new result feeds into, by row id: a result code from the file or the project; "" = none. */
  parents: z.record(z.string().max(60), z.string().trim().max(16)).optional(),
  /** IM-N4: the type chosen for each activity, by row id (left out = the type read from the file). */
  types: z.record(z.string().max(60), z.enum(ACTIVITY_TYPES)).optional(),
});
export type LogframeCommitResult = { results: number; activities: number; indicators: number };

/** R5 #7, #15a: what the logframe and its activity matrix say about one activity (GET /activities/:id/plan). */
export type ActivityPlanView = {
  /** The outputs (results) it feeds, main one first. */
  outputs: { id: Id; code: string; title: string }[];
  /** Its group's means and assumptions; null = not imported from a matrix. */
  plan: {
    groupCode: string;
    groupTitle: string;
    description: string;
    politicalInputs: string;
    technicalExpertise: string;
    operationalRequirements: string;
    assumptions: string[];
  } | null;
  costLines: { id: Id; description: string; amountCents: number | null; blRefs: string[]; budgetLine: { id: Id; label: string } | null }[];
  /** The cost lines' total; null when none has an amount. */
  totalCents: number | null;
};

export type ProjectTeam = {
  /** function: what they do on this project (Home per person); null = not said. Viewers have none. */
  members: {
    user: UserRef;
    role: "owner" | "lead" | "admin" | "member" | "viewer";
    label: string;
    fixed: boolean;
    function?: ProjectFunction | null;
    /** Fine-grained access (plan 2026-11-03): what a lead or admin took away; [] for owners, admins and the lead. */
    withheld: ProjectPermission[];
    /** Stands in for the contract lead (members only). */
    deputyLead: boolean;
    preset: PermissionPreset | "custom";
  }[];
  invitations: InvitationV3[];
  keyExperts: { engagementId: Id; expertId: Id; name: string; category: string; dayBudget: number; cv: "uploaded" | "missing" }[];
};
export const teamAddBody = z.object({ email, role: z.enum(["admin", "member", "viewer"]) });
export const teamRolePatch = z.object({ role: z.enum(["member", "viewer"]) });
/** What a team member can do on this project (plan 2026-11-03 fine-grained access). */
export const teamAccessPatch = z.object({
  withheld: z.array(z.enum(PROJECT_PERMISSIONS)).max(PROJECT_PERMISSIONS.length),
  deputyLead: z.boolean(),
});
/** What a team member does on this project (Home per person); null takes it off. */
export const teamFunctionPatch = z.object({ function: z.enum(PROJECT_FUNCTIONS).nullable() });

export type ProjectTorTemplate = {
  source: "org" | "uploaded" | "standard";
  sections: TemplateSection[];
  clauses: string;
  orgTemplateUsedOn: number;
  file: FileRef | null;
  /** Optional (WP projects): the sections each choice gives, for the Setup ToR preview. */
  choices?: { org: TemplateSection[] | null; standard: TemplateSection[] };
};
export const projectTorTemplateBody = z.object({
  source: z.enum(["org", "uploaded", "standard"]),
  fileId: uuid.optional(),
  sections: z.array(templateSection).max(LIMITS.sectionsMax).optional(),
  clauses: textOrEmpty(LIMITS.boilerplate),
});
export type Branding = { logo: FileRef | null; partnerLogos: FileRef[]; banner: FileRef | null; showEuStatement: boolean };
export const brandingBody = z.object({
  logoFileId: uuid.nullable(),
  partnerLogoFileIds: z.array(uuid).max(20),
  bannerFileId: uuid.nullable(),
  showEuStatement: z.boolean(),
});

// ---------- 6.2 running-project import ----------

export const importPile = z.enum(IMPORT_PILES);
export const startImportBody = z.object({ name: text(LIMITS.contractName, "Project name"), reference: text(LIMITS.contractReference, "Reference") });
/** pile: the kind of document, when the files went into one of the upload slots (Suite Onboard Upload); left out = sorted automatically. */
export const importFilesBody = z.object({ fileIds: z.array(uuid).min(1).max(500), pile: z.enum(IMPORT_PILES).optional() });
export type ImportJob = {
  id: Id;
  status: "uploading" | "sorting" | "review" | "confirmed" | "committed" | "failed";
  fileCount: number;
  sortedCount: number;
  piles: { pile: ImportPile; label: string; files: number; becomes: string }[];
  error: string | null;
  /** Optional (WP projects): the take-over settings once committed. */
  takeoverDate?: string | null;
  historyMode?: "readonly" | "editable_30" | null;
  /** AI import (docs/v3/AI-IMPORT.md 8): whether AI reads files, and how many used the simple rules and why. */
  ai: { on: boolean; readWithRules: number; reasons: Partial<Record<ImportFallbackReason, number>> };
};
export type ImportFallbackReason = "ai_off" | "budget" | "ai_error" | "unreadable" | "too_many_pages" | "recognised";
/** POST /import/commit: the committed job, and the attendance lists not read in because their activity is not an event (each file is attached to the activity). */
export type ImportCommitAnswer = ImportJob & {
  /** Lists not read in: the activity is not an event, or (reason "event_closed") its event is closed. */
  attendanceNotImported: { activityCode: string; path: string; reason?: "not_an_event" | "event_closed" }[];
};
/** Where the AI found a value: page and a short quote. */
export type ImportEvidence = { page: number | null; quote: string | null };
export type PileCount = { pile: ImportPile; label: string; count: number; toCheck: number; state: string };
export type ImportItem = {
  id: Id;
  pile: ImportPile;
  accepted: boolean;
  confidence: number;
  needsCheck: string | null;
  fields: Record<string, string | number | null>;
  options: Record<string, { value: string; label: string }[]>;
  sourceFile: FileRef;
  /** How the item was read (docs/v3/AI-IMPORT.md 8). */
  source: "ai" | "rules";
  evidence: Record<string, ImportEvidence>;
  /** Per-field confidence of AI values (0..1); below 0.6 the field needs a person. */
  fieldConfidence: Record<string, number>;
};
export const importItemsQuery = z.object({
  pile: importPile.optional(),
  needsCheck: z.enum(["1"]).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(LIMITS.pageMax).default(LIMITS.pageDefault),
});
export const importItemPatch = z.object({
  accepted: z.boolean().optional(),
  correction: z.record(z.string().max(60), z.union([z.string().max(2000), z.number(), z.null()])).optional(),
});
export type ImportFigures = {
  envelope: { cents: number | null; source: string };
  approved: { cents: number; tors: number };
  withFunder: { cents: number; tors: number };
  expertDays: { used: number; budget: number; timesheets: number };
  participants: { count: number; womenPct: number | null; lists: number };
  activities: { result: string; done: number; running: number; planned: number }[];
  indicators: { name: string; target: string; lastReading: string | null; from: string | null }[];
};
export const importConfirmBody = z.object({
  matchesRecords: z.literal(true),
  corrections: z.object({ envelopeCents: centsV3(LIMITS.envelopeCentsMax).optional(), expertDaysBudget: z.number().min(0).optional() }).optional(),
});
export type GapItem = {
  id: Id;
  kind: "no_attendance" | "no_tor" | "old_cv" | "possible_duplicates" | "missing_timesheets";
  description: string;
  fixLabel: string;
  fixPath: string;
  status: "open" | "deferred" | "resolved";
  /** Optional (WP projects): the one-line heading, e.g. "3 activities have no attendance list". */
  title?: string;
};
export const gapPatch = z.object({ status: z.enum(["deferred", "resolved"]) });
export type ImportSummary = { files: number; tors: number; stakeholders: number; experts: number; gaps: number };
export type TakeoverPreview = { colleagues: number; experts: number; funderEmail: string | null; summary: ImportSummary };
export const importCommitBody = z.object({
  takeoverDate: isoDate,
  historyMode: z.enum(["readonly", "editable_30"]),
  inviteColleagues: z.boolean(),
  emailExperts: z.boolean(),
  emailFunder: z.boolean(),
});
export type CallSlots = { slots: { value: string; label: string }[] };
export const onboardingCallBody = z.object({
  name: text(LIMITS.userName, "Name"),
  projectName: text(LIMITS.contractName, "Project name"),
  slot: z.string().max(64),
  fileId: uuid.nullable(),
  note: textOrEmpty(2000),
});

// ---------- 6.3 home, search, notifications, activity log ----------

export type AttentionItem = {
  id: string;
  module: string;
  code: string | null;
  title: string;
  amountCents: number | null;
  reason: string;
  path: string;
  deferrable: boolean;
  /**
   * Home per person. ownerUserId: the one person who acts (the to-do's
   * owner, the activity's owner, the follow-up's owner); left out or null =
   * whoever has the audience's function. audience: left out = "lead".
   * dueOn: the date it is due, for "Today / This week / Later"; null = no date.
   */
  ownerUserId?: string | null;
  /** Who acts when ownerUserId has left the team (a to-do's activity owner, a follow-up's meeting author); then the audience. */
  nextOwnerUserId?: string | null;
  audience?: AttentionAudience;
  dueOn?: string | null;
  /** Filled by Home: this item is the signed-in person's ("Mine"). */
  mine?: boolean;
  /** The item's one button, when it has its own words ("Continue the report", report builder board 6); else the title is the link. */
  action?: { label: string; path: string } | null;
  /** ST-F1 (audit round 4): a legal deadline that has passed (a data request): red, first in its group. */
  urgent?: boolean;
};
export const HOME_VIEWS = ["mine", "all"] as const;
export type HomeView = (typeof HOME_VIEWS)[number];
export const homeQuery = z.object({ view: z.enum(HOME_VIEWS).default("mine") });
/** GET /me/attention: my items in each project I can see (Home per person, across projects). */
export type MyAttention = {
  projects: { project: ProjectRef; count: number; items: AttentionItem[] }[];
  /** Only the first projects are read (bounded); true when there were more. */
  more: boolean;
  /**
   * Ruling R16: the sections of draft projects (still invisible on Home) assigned to me and not
   * confirmed yet, each opened at /projects/new/:projectId/review/:section. Left out = none.
   */
  drafts?: { project: ProjectRef; sections: OnboardingSection[] }[];
};
/** One row of Home's Setup card (board A6): an open item still to do (onboarding openItems), with its owner's name. */
export type HomeSetupItem = { key: string; section: OnboardingSection; title: string; detail: string; ownerId: string | null; ownerName: string | null };
/**
 * Home of a project set up through the onboarding (board A6): the Setup card while open items
 * are left (null once all are done), and the four tiles: the next report of the reporting calendar
 * (planned periods count as reports, ruling R25), the budget lines' total and what is spent (the
 * admin money readers, ADR 0001), the activities and how many start this month.
 */
/** OB-F5 (audit round 4): a setup checklist step still to do, as Administration › Setup checklist lists it, and where it is done. */
export type HomeSetupStep = { key: SetupCheckKey; label: string; detail: string; path: string };
export type HomeSetup = {
  /**
   * OB-F5: the setup checklist's steps still to do (the one count, the same as Administration ›
   * Setup checklist: `total - done` of `steps`); null once the checklist is complete. `items`: the
   * open items, kept for older clients; Home lists them under Needs your attention, with their owners.
   */
  card: { items: HomeSetupItem[]; steps?: HomeSetupStep[]; done?: number; total?: number } | null;
  nextReport: { kind: string; number: number; dueOn: string } | null;
  budget: { totalCents: number; spentCents: number };
  activities: { total: number; startingThisMonth: number };
};
export type GlanceTile = {
  key: "activities" | "expert_days" | "participants" | "stakeholders" | "indicators";
  label: string;
  value: string;
  sub: string;
  path: string;
  /** GL-F8 (D7): "How this is counted", in the words of the screen the tile opens (the same function computes both). */
  counted?: string;
};
export type Home = {
  project: ProjectRef & { typeLabel: string; period: { start: string | null; end: string | null }; lead: UserRef | null };
  greeting: string;
  money: {
    envelopeCents: number | null;
    cofinancingPct: number | null;
    approvedCents: number;
    approvedTors: number;
    onHoldCents: number;
    /** BU-N2: onHoldCents by where the ToRs stand (with the funder, signed off and not sent, changes requested). */
    onHoldSplit: WaitingSplit;
    onHoldTors: number;
    oldestOnHoldDays: number | null;
    /** Spent inside the periods of reports the funder has (as the Administration overview counts it). */
    reportedCents: number;
    /** Spent (approved, paid, fees from timesheets) and not yet in such a report. */
    notReportedCents: number;
    /** HO3: what approved ToRs, open contracts and closed events' final figures still hold (the one budget calculation). onHoldCents is what ToRs with the funder hold. */
    committedCents: number;
    lastReport: string | null;
    /** The contract total less reported, not yet reported, committed and on hold. */
    remainingCents: number | null;
    /**
     * AR-F1 (audit round 4): Administration's Budget status for the whole project (the same figures: spent,
     * committed, waiting, changes requested, planned work still to pay and "To be allocated", which add up to the
     * direct costs). Home draws that bar when it is set; null without budget lines (Home then shows "Left").
     */
    budgetStatus?: NonNullable<BudgetView["outlook"]> | null;
  };
  attention: AttentionItem[];
  comingUp: {
    date: string;
    title: string;
    subLine: string;
    /** "mission": an (N)KE activity (round 4 Home says "Mission"). */
    type: "activity" | "mission" | "event" | "report" | "deliverable" | "meeting";
    status: StatusChip;
    path: string;
  }[];
  glance: GlanceTile[];
  recent: HistoryEntry[];
  setupIncomplete: boolean;
  /** #13, GL6: the setup checklist's own count (the steps that count), so Home and the checklist agree; null once complete or for partners. */
  setupProgress?: { done: number; total: number } | null;
  /** Optional (WP projects): true for a consortium partner without "see budget" (money figures are zero). */
  moneyHidden?: boolean;
  /** Optional (WP projects): the caller may create activities and ToRs (Home actions). */
  canEdit?: boolean;
  /**
   * Home per person: "mine" shows only the signed-in person's items, "all"
   * everything the team has. counts: both sizes, for the switch. you: what
   * decides "mine" (contract lead, their function on this project).
   */
  view?: HomeView;
  counts?: { mine: number; all: number };
  you?: { isLead: boolean; function: ProjectFunction | null };
  /** "Today" in Brussels (YYYY-MM-DD): the page groups items by it (Today / This week / Later). */
  today?: string;
  /** Only for a project set up through the onboarding (left out or null otherwise): board A6. */
  setup?: HomeSetup | null;
};

export const SEARCH_TYPES = ["all", "activities", "tors", "stakeholders", "experts", "documents", "reports"] as const;
export const searchQueryV3 = z.object({
  q: z.string().trim().max(LIMITS.searchQuery).default(""),
  type: z.enum(SEARCH_TYPES).default("all"),
  scope: z.enum(["project", "all"]).default("project"),
});
export type SearchHit = {
  type: "activity" | "tor" | "stakeholder" | "organisation" | "expert" | "document" | "report";
  id: Id;
  title: string;
  meta: string;
  snippet: string;
  titleMatches: [number, number][];
  snippetMatches: [number, number][];
  path: string;
  projectName: string | null;
};
export type SearchResult = {
  q: string;
  scope: "project" | "all";
  projectName: string;
  total: number;
  counts: Record<(typeof SEARCH_TYPES)[number], number>;
  hits: SearchHit[];
};

export type NotificationV3 = {
  id: Id;
  kind: NotificationKind;
  title: string;
  body: string;
  path: string | null;
  read: boolean;
  createdAt: string;
  project: ProjectRef | null;
  /** GL-F11 (audit round 4): how urgent it is (notificationUrgency). Optional: older servers leave it out. */
  urgency?: NotificationUrgency;
};

/**
 * GL-F11 (audit round 4): Alerts, most urgent first. "deadline": a legal or contractual deadline
 * (a data request, a report due); "action": somebody waits for you; "info": for your information.
 */
export const NOTIFICATION_URGENCIES = ["deadline", "action", "info"] as const;
export type NotificationUrgency = (typeof NOTIFICATION_URGENCIES)[number];
export const NOTIFICATION_URGENCY_LABELS: Record<NotificationUrgency, string> = {
  deadline: "Deadlines",
  action: "Waiting for you",
  info: "For your information",
};
const URGENCY_OF: Record<NotificationKind, NotificationUrgency> = {
  data_request_due: "deadline",
  report_due: "deadline",
  tor_decision: "action",
  tor_signed_off: "action",
  timesheet_signed: "action",
  timesheet_question: "action",
  timesheet_ready: "action",
  document_to_sign: "action",
  declaration_reminder: "action",
  invoice_submitted: "action",
  claim_submitted: "action",
  deliverable_uploaded: "action",
  deliverable_changes_requested: "action",
  mention: "action",
  join_request: "action",
  expert_message: "action",
  iban_changed: "action",
  funder_document: "action",
  speaker_details_received: "action",
  attention_new: "action",
  tor_resubmitted: "info",
  claim_decided: "info",
  invoice_paid: "info",
  import_finished: "info",
  digest_daily: "info",
  digest_weekly: "info",
  ai_budget_reached: "info",
  guest_reply: "info",
};
export const notificationUrgency = (kind: NotificationKind): NotificationUrgency => URGENCY_OF[kind] ?? "info";
export const notificationsQuery = z.object({
  all: z.enum(["1"]).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(LIMITS.pageMax).default(LIMITS.pageDefault),
});
export const notificationsReadBody = z.union([z.object({ ids: z.array(uuid).min(1).max(500) }), z.object({ all: z.literal(true) })]);

export const activityLogQuery = z.object({
  module: z.enum(["all", ...AUDIT_MODULES]).default("all"),
  cursor: z.string().max(200).optional(),
});
export const historyQuery = z.object({
  entityType: z.string().max(40),
  entityId: uuid,
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type HistoryResponse = { entries: HistoryEntry[] };
